import type { CalendarEventData } from './add-to-calendar.interface';

const MAX_CONTENT_LINE_OCTETS = 75;
const CONTENT_LINE_BREAK = '\r\n';
const CONTENT_LINE_FOLD = '\r\n ';

export function generateGoogleCalendarUrl(data: CalendarEventData): string {
  const startDate = formatUtcStamp(data.startTime);
  const endDate = formatUtcStamp(data.endTime);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: data.title,
    dates: `${startDate}/${endDate}`,
    details: data.description,
  });

  if (data.location) {
    params.append('location', data.location);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function generateOutlookCalendarUrl(data: CalendarEventData): string {
  const params = new URLSearchParams({
    subject: data.title,
    startdt: data.startTime.toISOString(),
    enddt: data.endTime.toISOString(),
    body: data.description,
  });

  if (data.location) {
    params.append('location', data.location);
  }

  const query = params.toString();
  return `https://outlook.live.com/calendar/0/deeplink/compose?${query}`;
}

export function generateYahooCalendarUrl(data: CalendarEventData): string {
  const duration = Math.floor(
    (data.endTime.getTime() - data.startTime.getTime()) / 60,
  );

  const params = new URLSearchParams({
    v: '60',
    view: 'd',
    type: '20',
    title: data.title,
    st: formatUnixSeconds(data.startTime),
    dur: duration.toString(),
    desc: data.description,
  });

  if (data.location) {
    params.append('in_loc', data.location);
  }

  return `https://calendar.yahoo.com/?${params.toString()}`;
}

export function generateICSFile(data: CalendarEventData): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Appointment Booking by SMS Reminder//Booking System//EN',
    'BEGIN:VEVENT',
    `UID:${data.uid}`,
    `DTSTAMP:${formatUtcStamp(new Date())}`,
    `DTSTART:${formatUtcStamp(data.startTime)}`,
    `DTEND:${formatUtcStamp(data.endTime)}`,
    `SUMMARY:${escapeIcsText(data.title)}`,
    `DESCRIPTION:${escapeIcsText(data.description)}`,
  ];

  if (data.location) {
    lines.push(`LOCATION:${escapeIcsText(data.location)}`);
  }

  lines.push('END:VEVENT', 'END:VCALENDAR');

  return lines.map((line) => foldContentLine(line)).join(CONTENT_LINE_BREAK);
}

function formatUtcStamp(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

function formatUnixSeconds(date: Date): string {
  return Math.floor(date.getTime() / 1000).toString();
}

function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '')
    .replace(/\n/g, '\\n');
}

/**
 * RFC 5545 caps a content line at 75 octets, not characters, and the leading
 * space of a continuation line counts against the next line's budget.
 */
function foldContentLine(line: string): string {
  const segments: string[] = [];
  let current = '';
  let octets = 0;
  let budget = MAX_CONTENT_LINE_OCTETS;
  let index = 0;

  while (index < line.length) {
    const codePoint = line.codePointAt(index) ?? 0;
    const char = String.fromCodePoint(codePoint);
    const size = utf8OctetLength(codePoint);
    index += char.length;

    if (octets + size > budget) {
      segments.push(current);
      current = '';
      octets = 0;
      budget = MAX_CONTENT_LINE_OCTETS - 1;
    }

    current += char;
    octets += size;
  }

  segments.push(current);
  return segments.join(CONTENT_LINE_FOLD);
}

function utf8OctetLength(codePoint: number): number {
  if (codePoint <= 0x7f) return 1;
  if (codePoint <= 0x7ff) return 2;
  if (codePoint <= 0xffff) return 3;
  return 4;
}
